import { useCallback } from "react";

import Intercom from "@/crossPlatform/Intercom";
import { useLazyGetIntercomTokenQuery } from "@/store/api/integration";
import { useAppSelector } from "@/store/hooks";
import {
  selectTokenExpiresAt,
  useHelpCenterDispatch,
} from "@/store/reducers/helpCenter";
import { toMs } from "@/utils/date.util";

const isTokenValid = (tokenExpiresAt: null | number): boolean => {
  if (tokenExpiresAt === null) return false;
  const currentTime = Date.now();
  const timeUntilExpiry = tokenExpiresAt - currentTime;
  // Token is valid if it expires in more than 1 minute
  return timeUntilExpiry > toMs({ minutes: 1 });
};

interface UseIntercomTokenManager {
  clearTokenData: () => void;
  ensureTokenIsSet: () => Promise<void>;
}

const useIntercomTokenManager = (): UseIntercomTokenManager => {
  const { clearTokenData: clearTokenExpiry, setTokenExpiresAt } =
    useHelpCenterDispatch();
  const tokenExpiresAt = useAppSelector(selectTokenExpiresAt);

  // Fetch JWT token using RTK Query
  const [fetchIntercomToken] = useLazyGetIntercomTokenQuery();

  const clearTokenData = useCallback((): void => {
    clearTokenExpiry();
  }, [clearTokenExpiry]);

  const ensureTokenIsSet = useCallback(async (): Promise<void> => {
    // If we already have valid token data, nothing to do
    if (isTokenValid(tokenExpiresAt)) {
      return;
    }

    // Fetch new token when we don't have valid token data
    const result = await fetchIntercomToken();
    if (result.data) {
      const expiresAtTimestamp = new Date(result.data.expiresAt).getTime();
      setTokenExpiresAt(expiresAtTimestamp);
      await Intercom.setUserJwt(result.data.token);
    }
  }, [fetchIntercomToken, setTokenExpiresAt, tokenExpiresAt]);

  return {
    clearTokenData,
    ensureTokenIsSet,
  };
};

export default useIntercomTokenManager;
